import { useEffect } from "react"
import { useApiQuery } from "@/lib/api/hooks"
import { UnitCard } from "@/components/UnitCard"
import { useClerk } from "@clerk/clerk-react"
import { useNavigate } from "react-router-dom"

export function DashboardPage() {
  const navigate = useNavigate()
  const { signOut } = useClerk()
  const { data: subjects, isPending } = useApiQuery("get", "/dashboard/subjects")
  const { data: dueData } = useApiQuery("get", "/fsrs/due-count", undefined, {
    staleTime: 0,
    refetchOnMount: "always",
  })
  const dueCount = dueData?.dueCount ?? 0

  // First-time users (or anyone who's been signed-out-and-deleted) land here
  // with zero opted-in subjects. Send them to the picker.
  useEffect(() => {
    if (!isPending && subjects && subjects.length === 0) {
      navigate("/subjects", { replace: true })
    }
  }, [isPending, subjects, navigate])

  async function handleSignOut() {
    await signOut()
    navigate("/login")
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold">Apollo</h1>
        <div className="flex items-center gap-5">
          <button
            onClick={() => navigate("/subjects")}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Learn New Subject
          </button>
          <button
            onClick={() => navigate("/review")}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1.5"
          >
            Review
            {dueCount > 0 && (
              <span className="rounded-full bg-primary text-primary-foreground text-xs font-semibold px-1.5 py-0.5 min-w-[1.25rem] text-center">
                {dueCount}
              </span>
            )}
          </button>
          <button
            onClick={handleSignOut}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="px-6 py-8 max-w-5xl mx-auto">
        {dueCount > 0 && (
          <div className="mb-8 rounded-lg border border-primary/30 bg-primary/5 p-5 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold mb-1">Time to review</h2>
              <p className="text-sm text-muted-foreground">
                You have {dueCount} card{dueCount === 1 ? "" : "s"} due for review. A quick session keeps everything fresh.
              </p>
            </div>
            <button
              onClick={() => navigate("/review")}
              className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Start review
            </button>
          </div>
        )}

        {isPending ? (
          <div className="flex items-center justify-center py-24">
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : !subjects?.length ? (
          // Empty state is brief — the redirect effect above takes over.
          <div className="flex items-center justify-center py-24">
            <span className="text-sm text-muted-foreground">Loading…</span>
          </div>
        ) : (
          <div className="space-y-10">
            {subjects.map((subject) => (
              <section key={subject.id}>
                <h2 className="text-xl font-semibold mb-1">{subject.name}</h2>
                {subject.description && (
                  <p className="text-sm text-muted-foreground mb-5">{subject.description}</p>
                )}

                {subject.units.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No units yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-6">
                    {subject.units.map((unit) => (
                      <UnitCard
                        key={unit.id}
                        id={unit.id}
                        name={unit.name}
                        completedTopics={Number(unit.completedTopics)}
                        totalTopics={Number(unit.totalTopics)}
                        onClick={() => navigate(`/unit/${unit.id}`)}
                      />
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
